import React, { useContext } from "react";
import { AlertCircle, CheckCircle2, Search } from "lucide-react";
import { Button } from "components/ui/button";
import { InfiniteListContext } from "context/InfiniteList/Context";
import useInfiniteList from "hooks/useInfiniteList";

export interface InfiniteListAreaLabels {
    errorTitle: string;
    errorDescription: string;
    retry: string;
    emptyTitle: string;
    emptyDescription: string;
    resetFilters: string;
    endOfList?: string;
}

type InfiniteListAreaProps<T> = {
    skeleton: React.ReactNode;
    labels: InfiniteListAreaLabels;
    emptyContent?: React.ReactNode;
    children: (items: T[]) => React.ReactNode;
};

/**
 * Self-contained infinite-scroll list area.
 */
function InfiniteListArea<T>({ skeleton, labels, emptyContent, children }: InfiniteListAreaProps<T>) {
    const { hasError, refreshData } = useContext(InfiniteListContext);

    const { displayedItems, isFullShimmering, isLoadMoreShimmering, hasMore, sentinelRef, handleResetFilters } =
        useInfiniteList<T>();

    const hasItems = displayedItems.length > 0;

    const initialLoadingOrFilterReset = !hasError && isFullShimmering;
    const loadedCards = !hasError && !isFullShimmering && hasItems;
    const loadingMoreSkeleton = !hasError && isLoadMoreShimmering;
    const emptyState = !hasError && !isFullShimmering && !isLoadMoreShimmering && !hasItems;

    const showEndMessage =
        Boolean(labels.endOfList) &&
        hasItems &&
        !hasMore &&
        !hasError &&
        !isFullShimmering &&
        !isLoadMoreShimmering;

    return (
        <div>
            {hasError && (
                <div className="flex flex-col items-center gap-4 rounded-3xl border border-destructive/20 bg-destructive/5 px-6 py-12 text-center">
                    <AlertCircle className="h-10 w-10 text-destructive" />
                    <div>
                        <p className="font-semibold text-destructive">{labels.errorTitle}</p>
                        <p className="mt-1 text-sm text-muted-foreground">{labels.errorDescription}</p>
                    </div>
                    <Button onClick={refreshData}>{labels.retry}</Button>
                </div>
            )}

            {initialLoadingOrFilterReset && skeleton}

            {loadedCards && children(displayedItems)}

            {loadingMoreSkeleton && <div className="mt-6">{skeleton}</div>}

            {emptyState &&
                (emptyContent ?? (
                    <div className="flex flex-col items-center gap-4 rounded-3xl border border-dashed px-6 py-16 text-center">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                            <Search className="h-7 w-7 text-muted-foreground" />
                        </div>
                        <div>
                            <p className="font-semibold">{labels.emptyTitle}</p>
                            <p className="mt-1 text-sm text-muted-foreground">{labels.emptyDescription}</p>
                        </div>
                        <Button variant="outline" onClick={handleResetFilters}>
                            {labels.resetFilters}
                        </Button>
                    </div>
                ))}

            {hasItems && !hasError && (
                <div className="mt-10 flex flex-col items-center gap-3">
                    <div ref={sentinelRef} aria-hidden="true" />

                    {showEndMessage && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                            {labels.endOfList}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default InfiniteListArea;
